import React, { useState, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CTA_TYPES, CTA_OPTIONS } from "@/lib/adCta";
import { Plus, Trash2, Target, Users, Loader2, AlertTriangle } from "lucide-react";

// Shared CTA (action button) + audience targeting fields. Used by both the
// user Ads Manager and the admin offline-ad form so they stay in parity.
// The parent `form` MUST include: cta_type, cta_label, cta_value, cta_headline,
// cta_description, lead_fields[], custom_fields[], t_genders[], t_age_min,
// t_age_max, t_countries, t_cities, t_denominations[], t_interests[].
export const AdCreativeExtras = ({ form, setForm }) => {
  const { config, lang, t } = useApp();
  const denominations = config?.denominations || [];
  const interests = config?.interests || [];
  const [showTargeting, setShowTargeting] = useState(false);

  // Live "potential audience" estimate (Facebook-style reach meter) that reacts to targeting.
  const [estimate, setEstimate] = useState(null);
  const [estLoading, setEstLoading] = useState(false);
  useEffect(() => {
    if (!showTargeting) return;
    const tg = {
      genders: form.t_genders || [],
      age_min: Number(form.t_age_min) || 0,
      age_max: Number(form.t_age_max) || 0,
      countries: (form.t_countries || "").split(",").map((s) => s.trim()).filter(Boolean),
      cities: (form.t_cities || "").split(",").map((s) => s.trim()).filter(Boolean),
      denominations: form.t_denominations || [],
      interests: form.t_interests || [],
      churches: (form.t_churches || []).map((c) => c.name),
      church_ids: (form.t_churches || []).map((c) => c.id),
    };
    setEstLoading(true);
    const id = setTimeout(() => {
      api.post("/ads/estimate", { targeting: tg, days: 7 })
        .then(({ data }) => setEstimate(data))
        .catch(() => setEstimate(null))
        .finally(() => setEstLoading(false));
    }, 450);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTargeting, form.t_genders, form.t_age_min, form.t_age_max, form.t_countries, form.t_cities, form.t_denominations, form.t_interests, form.t_churches]);

  const nfmt = (n) => (n || 0).toLocaleString(lang === "fr" ? "fr-FR" : "en-US");
  const reachPct = estimate && estimate.reachable
    ? Math.max(3, Math.min(100, Math.round((estimate.audience / estimate.reachable) * 100)))
    : 100;
  const defLabel = reachPct < 20
    ? t("ads.audience_narrow", "Audience précise")
    : reachPct < 60
      ? t("ads.audience_medium", "Audience moyenne")
      : t("ads.audience_broad", "Audience large");
  const tooNarrow = !!estimate && estimate.audience > 0 && estimate.audience < 20;
  const breakdown = estimate?.breakdown || [];
  const bdMax = Math.max(1, ...breakdown.map((b) => b.total || 0));
  const hasBreakdown = breakdown.some((b) => (b.total || 0) > 0);
  const applyReco = () => {
    if (!estimate) return;
    setForm((f) => ({
      ...f,
      days: estimate.recommended_days,
      ...("manual_price" in f ? { manual_price: estimate.recommended_budget } : {}),
    }));
  };

  const toggleArr = (key, val) => setForm((f) => ({
    ...f,
    [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
  }));
  const toggleLeadField = (k) => setForm((f) => ({
    ...f,
    lead_fields: f.lead_fields.includes(k) ? f.lead_fields.filter((x) => x !== k) : [...f.lead_fields, k],
  }));
  const addCustomField = () => setForm((f) => (f.custom_fields.length >= 5 ? f : {
    ...f,
    custom_fields: [...f.custom_fields, { id: `q${Date.now()}`, label: "", type: "text", options: "", required: false }],
  }));
  const updateCustomField = (i, patch) => setForm((f) => ({ ...f, custom_fields: f.custom_fields.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));
  const removeCustomField = (i) => setForm((f) => ({ ...f, custom_fields: f.custom_fields.filter((_, j) => j !== i) }));

  const LEAD_FIELD_OPTS = [
    { key: "name", label: t("auth.name", "Nom") },
    { key: "email", label: t("auth.email", "E-mail") },
    { key: "phone", label: t("ads.cta_phone_label", "Téléphone") },
    { key: "message", label: t("comment.placeholder", "Message") },
  ];

  return (
    <>
      {/* Bouton d'action (CTA) façon Facebook */}
      <div className="rounded-xl border p-3 space-y-3" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }} data-testid="ad-cta-config">
        <div className="text-sm font-bold">{t("ads.cta_section", "Bouton d'action (comme sur Facebook)")}</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>{t("ads.cta_type_label", "Type de bouton")}</Label>
            <select value={form.cta_type} onChange={(e) => setForm({ ...form, cta_type: e.target.value })} className="w-full h-10 rounded-xl border px-3 text-sm bg-white" style={{ borderColor: "var(--yalola-border)" }} data-testid="ad-cta-type">
              {CTA_TYPES.map((o) => <option key={o.value} value={o.value}>{t(`ads.ctatype_${o.value}`, o.label)}</option>)}
            </select>
          </div>
          {form.cta_type !== "none" && (
            <div>
              <Label>{t("ads.cta_label_label", "Libellé du bouton")}</Label>
              <select value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} className="w-full h-10 rounded-xl border px-3 text-sm bg-white" style={{ borderColor: "var(--yalola-border)" }} data-testid="ad-cta-label">
                {CTA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(`ads.ctalabel_${o.value}`, o.label)}</option>)}
              </select>
            </div>
          )}
        </div>
        {form.cta_type === "url" && (
          <div><Label>{t("ads.cta_link_label", "Lien du bouton")}</Label><Input value={form.cta_value} onChange={(e) => setForm({ ...form, cta_value: e.target.value })} placeholder={t("ads.cta_link_ph", "https://votre-site.com/devis")} data-testid="ad-cta-value" /></div>
        )}
        {form.cta_type === "whatsapp" && (
          <div><Label>{t("ads.cta_whatsapp_label", "Numéro WhatsApp (indicatif compris)")}</Label><Input value={form.cta_value} onChange={(e) => setForm({ ...form, cta_value: e.target.value })} placeholder={t("ads.phone_ph", "+32470000000")} data-testid="ad-cta-value" /></div>
        )}
        {form.cta_type === "phone" && (
          <div><Label>{t("ads.cta_phone_label", "Numéro de téléphone")}</Label><Input value={form.cta_value} onChange={(e) => setForm({ ...form, cta_value: e.target.value })} placeholder={t("ads.phone_ph", "+32470000000")} data-testid="ad-cta-value" /></div>
        )}
        {form.cta_type === "lead_form" && (
          <div>
            <Label>{t("ads.lead_fields_label", "Champs du formulaire à collecter")}</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {LEAD_FIELD_OPTS.map((f) => {
                const on = form.lead_fields.includes(f.key);
                return (
                  <button key={f.key} type="button" onClick={() => toggleLeadField(f.key)} className={`pill ${on ? "pill-primary" : "pill-outline"}`} data-testid={`ad-lead-field-${f.key}`}>
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {form.cta_type === "lead_form" && (
          <div data-testid="ad-custom-fields">
            <div className="flex items-center justify-between">
              <Label>{t("ads.custom_q_label", "Questions personnalisées")} <span className="font-normal" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.custom_q_max", "(max 5)")}</span></Label>
              {form.custom_fields.length < 5 && (
                <button type="button" onClick={addCustomField} className="text-xs font-semibold inline-flex items-center gap-1" style={{ color: "var(--yalola-primary)" }} data-testid="ad-custom-add">
                  <Plus size={13} /> {t("ads.custom_q_add", "Ajouter une question")}
                </button>
              )}
            </div>
            <div className="space-y-2 mt-1.5">
              {form.custom_fields.length === 0 && (
                <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.custom_q_hint", "Ajoutez vos propres questions (texte, nombre ou liste déroulante).")}</p>
              )}
              {form.custom_fields.map((c, i) => (
                <div key={i} className="rounded-xl border p-2.5 space-y-2 bg-white" style={{ borderColor: "var(--yalola-border)" }} data-testid={`ad-custom-field-${i}`}>
                  <div className="flex items-center gap-2">
                    <Input value={c.label} onChange={(e) => updateCustomField(i, { label: e.target.value })} placeholder={t("ads.custom_q_ph", "Intitulé de la question")} className="flex-1" data-testid={`ad-custom-label-${i}`} />
                    <select value={c.type} onChange={(e) => updateCustomField(i, { type: e.target.value })} className="h-10 rounded-xl border px-2 text-sm bg-white shrink-0" style={{ borderColor: "var(--yalola-border)" }} data-testid={`ad-custom-type-${i}`}>
                      <option value="text">{t("ads.custom_type_text", "Texte")}</option>
                      <option value="number">{t("ads.custom_type_number", "Nombre")}</option>
                      <option value="select">{t("ads.custom_type_select", "Liste déroulante")}</option>
                    </select>
                    <button type="button" onClick={() => removeCustomField(i)} className="h-9 w-9 grid place-items-center rounded-lg hover:bg-red-50 text-red-600 shrink-0" data-testid={`ad-custom-remove-${i}`}><Trash2 size={15} /></button>
                  </div>
                  {c.type === "select" && (
                    <Input value={c.options} onChange={(e) => updateCustomField(i, { options: e.target.value })} placeholder={t("ads.custom_options_ph", "Options séparées par des virgules (ex : Oui, Non, Peut-être)")} data-testid={`ad-custom-options-${i}`} />
                  )}
                  <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: "var(--yalola-text-muted)" }}>
                    <input type="checkbox" checked={c.required} onChange={(e) => updateCustomField(i, { required: e.target.checked })} data-testid={`ad-custom-required-${i}`} /> {t("ads.custom_required", "Obligatoire")}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>{t("ads.cta_headline_label", "Titre du bouton (gras)")}</Label><Input value={form.cta_headline} onChange={(e) => setForm({ ...form, cta_headline: e.target.value })} placeholder={t("ads.cta_headline_ph", "Démolition & Déblaiement — Liège")} data-testid="ad-cta-headline" /></div>
          <div><Label>{t("ads.cta_subtitle_label", "Sous-titre")}</Label><Input value={form.cta_description} onChange={(e) => setForm({ ...form, cta_description: e.target.value })} placeholder={t("ads.cta_subtitle_ph", "Devis gratuit sous 48h")} data-testid="ad-cta-description" /></div>
        </div>
      </div>

      {/* Ciblage intelligent (facultatif) */}
      <div className="rounded-xl border p-3" style={{ borderColor: "var(--yalola-border)" }} data-testid="ad-targeting-config">
        <button type="button" onClick={() => setShowTargeting((v) => !v)} className="w-full flex items-center justify-between text-sm font-bold" data-testid="ad-targeting-toggle">
          <span className="flex items-center gap-1.5"><Target size={15} /> {t("ads.targeting_toggle", "Affiner l'audience (facultatif)")}</span>
          <span className="text-lg leading-none">{showTargeting ? "−" : "+"}</span>
        </button>
        {showTargeting && (
          <div className="space-y-3 mt-3">
            <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
              {t("ads.targeting_hint", "Laissez vide pour toucher tout le monde. Sexe, âge, pays et villes filtrent l'audience ; obédiences et centres d'intérêt améliorent la pertinence.")}
            </p>

            {/* Facebook-style potential-reach meter — updates live as targeting changes */}
            <div className="rounded-xl p-3 border" style={{ borderColor: "var(--yalola-primary)", background: "var(--yalola-primary-soft)" }} data-testid="ad-audience-estimate">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-wide flex items-center gap-1.5" style={{ color: "var(--yalola-primary)" }}>
                  <Users size={13} /> {t("ads.audience_potential", "Audience potentielle")}
                </span>
                {estLoading && <Loader2 size={13} className="animate-spin" style={{ color: "var(--yalola-primary)" }} />}
              </div>
              <div className="mt-1 flex items-baseline gap-1.5">
                <span className="text-2xl font-extrabold" data-testid="ad-estimate-count" style={{ color: "var(--yalola-text)" }}>
                  {estimate ? `~${nfmt(estimate.audience)}` : "—"}
                </span>
                <span className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.estimate_members", "membres verront cette pub")}</span>
              </div>
              {estimate && (
                <>
                  <div className="h-1.5 rounded-full mt-2 overflow-hidden" style={{ background: "rgba(0,0,0,0.08)" }}>
                    <div className="h-full rounded-full transition-[width] duration-300" style={{ width: `${reachPct}%`, background: "var(--yalola-primary)" }} data-testid="ad-estimate-gauge" />
                  </div>
                  <div className="flex items-center justify-between text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>
                    <span data-testid="ad-estimate-definition">{defLabel}</span>
                    <span>{t("ads.estimate_of", "sur")} {nfmt(estimate.reachable)} {t("ads.estimate_reachable", "atteignables")}</span>
                  </div>
                </>
              )}

              {/* Narrow-audience alert */}
              {tooNarrow && (
                <div className="rounded-lg px-2.5 py-2 mt-2 text-[11px] flex items-start gap-1.5" style={{ background: "rgba(245,158,11,0.15)", color: "#b45309" }} data-testid="ad-audience-narrow-warning">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  <span>{t("ads.audience_too_narrow", "Audience trop étroite : élargissez votre ciblage (âge, pays, obédiences…) pour toucher plus de membres.")}</span>
                </div>
              )}

              {/* Recommended budget / duration + apply */}
              {estimate && (
                <div className="flex items-center justify-between gap-2 mt-2 rounded-lg px-2.5 py-2" style={{ background: "rgba(0,0,0,0.04)" }} data-testid="ad-reco-budget">
                  <div className="text-[11px] min-w-0">
                    <div className="font-bold" style={{ color: "var(--yalola-text)" }}>{t("ads.reco_title", "Conseillé")} : {estimate.recommended_budget}{estimate.currency} · {estimate.recommended_days} {t("ads.reco_days", "j")}</div>
                    <div className="truncate" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.reco_hint", "Budget et durée conseillés pour ce ciblage")}</div>
                  </div>
                  <button type="button" onClick={applyReco} className="chip chip-primary text-[11px] shrink-0" data-testid="ad-reco-apply">{t("ads.reco_apply", "Appliquer")}</button>
                </div>
              )}
            </div>

            {/* Facebook-style age × gender distribution mini-graph */}
            {hasBreakdown && (
              <div className="rounded-xl p-3 border" style={{ borderColor: "var(--yalola-border)" }} data-testid="ad-audience-breakdown">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.breakdown_title", "Répartition par âge")}</span>
                  <div className="flex items-center gap-2.5 text-[10px]" style={{ color: "var(--yalola-text-muted)" }}>
                    <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full" style={{ background: "var(--yalola-primary)" }} />{t("ads.breakdown_men", "Hommes")}</span>
                    <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full" style={{ background: "#ec4899" }} />{t("ads.breakdown_women", "Femmes")}</span>
                  </div>
                </div>
                <div className="flex items-end justify-between gap-1.5" style={{ height: 88 }}>
                  {breakdown.map((b) => (
                    <div key={b.label} className="flex-1 flex flex-col items-center gap-1 min-w-0" data-testid={`ad-breakdown-bar-${b.label}`}>
                      <div className="w-full flex items-end justify-center gap-[3px]" style={{ height: 64 }}>
                        <div className="w-2 rounded-t transition-[height] duration-300" title={`${b.male} · ${b.label}`} style={{ height: `${Math.round((b.male / bdMax) * 100)}%`, minHeight: b.male > 0 ? 3 : 0, background: "var(--yalola-primary)" }} />
                        <div className="w-2 rounded-t transition-[height] duration-300" title={`${b.female} · ${b.label}`} style={{ height: `${Math.round((b.female / bdMax) * 100)}%`, minHeight: b.female > 0 ? 3 : 0, background: "#ec4899" }} />
                      </div>
                      <span className="text-[8px] leading-none" style={{ color: "var(--yalola-text-muted)" }}>{b.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label>{t("ads.gender_label", "Sexe")}</Label>
              <div className="flex gap-2 mt-1">
                {[["male", t("ads.gender_male", "Hommes")], ["female", t("ads.gender_female", "Femmes")]].map(([v, l]) => (
                  <button type="button" key={v} onClick={() => toggleArr("t_genders", v)} className={`pill ${form.t_genders.includes(v) ? "pill-primary" : "pill-outline"}`} data-testid={`ad-target-gender-${v}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("ads.age_min", "Âge min")}</Label><Input type="number" min="0" value={form.t_age_min} onChange={(e) => setForm({ ...form, t_age_min: e.target.value })} placeholder="0" data-testid="ad-target-age-min" /></div>
              <div><Label>{t("ads.age_max", "Âge max")}</Label><Input type="number" min="0" value={form.t_age_max} onChange={(e) => setForm({ ...form, t_age_max: e.target.value })} placeholder={t("ads.age_max_ph", "0 = aucun")} data-testid="ad-target-age-max" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("ads.countries_label", "Pays (virgules)")}</Label><Input value={form.t_countries} onChange={(e) => setForm({ ...form, t_countries: e.target.value })} placeholder="BE, FR, CD" data-testid="ad-target-countries" /></div>
              <div><Label>{t("ads.cities_label", "Villes (virgules)")}</Label><Input value={form.t_cities} onChange={(e) => setForm({ ...form, t_cities: e.target.value })} placeholder="Bruxelles, Paris" data-testid="ad-target-cities" /></div>
            </div>
            {denominations.length > 0 && (
              <div>
                <div className="flex items-center justify-between gap-2">
                  <Label>{t("ads.denominations_label", "Obédiences ciblées")}</Label>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setForm((f) => ({ ...f, t_denominations: denominations.map((d) => d.name) }))} className="text-[11px] font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid="ad-denom-select-all">{t("ads.select_all", "Tout sélectionner")}</button>
                    {form.t_denominations.length > 0 && (
                      <button type="button" onClick={() => setForm((f) => ({ ...f, t_denominations: [] }))} className="text-[11px] font-semibold" style={{ color: "var(--yalola-text-muted)" }} data-testid="ad-denom-clear-all">{t("ads.deselect_all", "Tout désélectionner")}</button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1 max-h-28 overflow-y-auto" data-testid="ad-target-denominations">
                  {denominations.map((d) => (
                    <button type="button" key={d.id} onClick={() => toggleArr("t_denominations", d.name)} className={`pill text-xs ${form.t_denominations.includes(d.name) ? "pill-primary" : "pill-outline"}`}>{d.name}</button>
                  ))}
                </div>
              </div>
            )}
            {interests.length > 0 && (
              <div>
                <div className="flex items-center justify-between gap-2">
                  <Label>{t("ads.interests_label", "Centres d'intérêt")}</Label>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setForm((f) => ({ ...f, t_interests: interests.map((i) => i.key) }))} className="text-[11px] font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid="ad-interest-select-all">{t("ads.select_all", "Tout sélectionner")}</button>
                    {form.t_interests.length > 0 && (
                      <button type="button" onClick={() => setForm((f) => ({ ...f, t_interests: [] }))} className="text-[11px] font-semibold" style={{ color: "var(--yalola-text-muted)" }} data-testid="ad-interest-clear-all">{t("ads.deselect_all", "Tout désélectionner")}</button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1 max-h-28 overflow-y-auto" data-testid="ad-target-interests">
                  {interests.map((i) => (
                    <button type="button" key={i.key} onClick={() => toggleArr("t_interests", i.key)} className={`pill text-xs ${form.t_interests.includes(i.key) ? "pill-primary" : "pill-outline"}`}>{i.labels?.[lang] || i.key}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default AdCreativeExtras;
